import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { cn } from '../lib/utils';

const alertVariants = cva(
  'relative flex gap-3 rounded-md border p-3 font-sans text-base',
  {
    variants: {
      variant: {
        info: 'bg-info-bg border-info/25 text-info',
        success: 'bg-success-bg border-success/25 text-success',
        warning: 'bg-warning-bg border-warning/25 text-warning',
        danger: 'bg-danger-bg border-danger/25 text-danger',
      },
    },
    defaultVariants: { variant: 'info' },
  }
);

const icons = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  danger: AlertCircle,
} as const;

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /** Renders a dismiss control. Omit for an alert the user cannot clear. */
  onClose?: () => void;
}

const Alert = ({ className, variant, onClose, children, ...props }: AlertProps) => {
  const Icon = icons[variant ?? 'info'];
  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <Icon aria-hidden className="mt-0.5 size-[18px] shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="-m-1 shrink-0 self-start rounded-sm p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-current outline-none"
        >
          <X aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
};

export { Alert, alertVariants };
